import { create } from "zustand";

import { WorkspaceSimpleRoleType } from "src/clients/taktile-api";
import { isRoleDowngradeOrSame } from "src/hooks/useCapabilities/roleHierarchy";
import { isDevToolsAvailable } from "src/utils/environment";
import { logger } from "src/utils/logger";

type RoleOverrideState = {
  overrideWorkspaceRole: Nullable<WorkspaceSimpleRoleType>;
  overrideFWDOrAdmin: boolean;
};

type RoleOverrideActions = {
  setOverrideRole: (
    workspaceRole: WorkspaceSimpleRoleType,
    isFWDOrAdmin: boolean,
    actualWorkspaceRole: Nullable<WorkspaceSimpleRoleType>,
  ) => void;
  clearOverrideRole: () => void;
};

type RoleOverrideStore = ZustandStore<RoleOverrideState, RoleOverrideActions>;

const useRoleOverrideStore = create<RoleOverrideStore>((set) => ({
  overrideWorkspaceRole: null,
  overrideFWDOrAdmin: false,
  actions: {
    setOverrideRole: (workspaceRole, isFWDOrAdmin, actualWorkspaceRole) => {
      // Security check: only allow role override on dev environment
      if (!isDevToolsAvailable()) {
        logger.error(
          `Security: [frontend] Role override not allowed on ${window.location.hostname}`,
        );
        return;
      }

      // Security check: only allow downgrades or same role
      if (!actualWorkspaceRole) {
        logger.error("Cannot set role override: actual workspace role is null");
        return;
      }

      if (!isRoleDowngradeOrSame(actualWorkspaceRole, workspaceRole)) {
        logger.error(
          `Security: [frontend] Attempted privilege escalation from ${actualWorkspaceRole} to ${workspaceRole}`,
        );
        return;
      }

      set({
        overrideWorkspaceRole: workspaceRole,
        overrideFWDOrAdmin: isFWDOrAdmin,
      });
    },
    clearOverrideRole: () => {
      set({
        overrideWorkspaceRole: null,
        overrideFWDOrAdmin: false,
      });
    },
  },
}));

export const useRoleOverrideActions = () =>
  useRoleOverrideStore((state) => state.actions);

export const useRoleOverride = () =>
  useRoleOverrideStore((state) => ({
    overrideWorkspaceRole: state.overrideWorkspaceRole,
    overrideFWDOrAdmin: state.overrideFWDOrAdmin,
  }));
